import { useCallback, useEffect, useRef, useState } from "react";
import type { OgeTopic } from "@edu/shared";
import { getGeneratorTopics } from "./topics-api.ts";
import type { TopicSubject } from "./subject.ts";

/**
 * Справочник тем одного предмета: сам справочник, признак отказа и повтор.
 *
 * ⚠️ Вырезано 03.09.2026 по готовому шву из `use-topic-picks.ts`, который
 * тогда жил в `features/homework/assign/`, а 30.09.2026 переехал сюда, рядом.
 * Там осталось всё, что про строки-пики (количество, потолки, предпросмотр
 * состава), а сюда уехала ровно загрузка: её спрашивает и выбор одной темы в
 * комнате урока, который про строки-пики не знает ничего.
 *
 * ⚠️ Справочник грузится СРАЗУ, а не по нажатию. Причина в состояниях: пока он
 * не приехал, неизвестно, можно ли вообще предлагать этот источник, а
 * выключенный контрол обязан объяснить, почему он выключен. Ответ кэшируется на
 * час, значит второй заход запроса не делает.
 */

export interface TopicCatalog {
  /**
   * `null` означает «ещё грузится», и это НЕ то же самое, что пустой массив:
   * пустой это «тем нет». Ошибка здесь стоит выключенной ветки у того, кто
   * быстрее сети.
   */
  readonly topics: OgeTopic[] | null;
  /** Запрос не удался. Помогает повтор, и только он. */
  readonly failed: boolean;
  readonly reload: () => void;
}

/**
 * @param subject Чей справочник спрашивать. ⚠️ `null` означает «не спрашивать
 * вовсе», и это не то же самое, что «ещё не решили». Так выглядит предмет без
 * генераторов: у русского справочника нет, ручка отвечает на него
 * `invalid_query`, и запрос дал бы отказ, который нечем починить. Форма выдачи
 * домашки `null` не передаёт никогда: у неё по хуку на предмет, и предмет у
 * экземпляра постоянный.
 */
export function useTopicCatalog(subject: TopicSubject | null): TopicCatalog {
  const [topics, setTopics] = useState<OgeTopic[] | null>(null);
  const [failed, setFailed] = useState(false);
  /**
   * Номер запроса. ⚠️ Нужен из-за того, что предмет у ЭТОГО хука сменный:
   * комната переключает его одной ручкой, ответы возвращаются в порядке сети, и
   * без номера поздний ответ математики лёг бы в открытый английский. Ровно тот
   * класс тихой подмены, от которого стоит `isForeignTopics`, только источник
   * чужих данных свой собственный.
   */
  const token = useRef(0);

  const load = useCallback(() => {
    const mine = (token.current += 1);
    setFailed(false);
    // Справочник прошлого предмета гасится ДО запроса: показывать английские
    // темы под выбранной математикой хуже, чем показывать «Загрузка тем…»
    setTopics(null);
    if (subject === null) return;
    void (async () => {
      try {
        const res = await getGeneratorTopics(subject);
        if (token.current !== mine) return;
        setTopics(res.topics);
      } catch {
        // Один сломанный запрос не уносит весь экран: ветка показывается
        // выключенной с кнопкой «Повторить», соседние источники работают
        if (token.current !== mine) return;
        setFailed(true);
      }
    })();
  }, [subject]);

  useEffect(load, [load]);

  return { topics, failed, reload: load };
}
